import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {makeAutoObservable, observable} from "mobx";
import {ModalType, RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {ITeamStore} from "data/stores/team/team.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import {IMargin} from "data/providers/api/team.api.provider";
import type {IModalsStore} from "data/stores/modals/modals.store";

export interface ITiebreakerModalController extends ViewController {
	get i18n(): ILocalizationStore;
	get isVisible(): boolean;
	get tiebreakerPoints(): number;
	get tiebreakerValue(): number;
	get margin(): IMargin;

	closeModal: () => void;
	setMargin: (marginUs: number) => void;
	onSaveTiebreaker: () => void;
}

@injectable()
export class TiebreakerModalController implements ITiebreakerModalController {
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _tiebreakerValue: number = 0;
	// private _disposerReaction!: ReturnType<typeof reaction>;

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore
	) {
		makeAutoObservable(this);
	}

	get i18n() {
		return this._i18nStore;
	}

	get isVisible() {
		return this._teamStore.isTiebreakerVisible;
	}

	get tiebreakerPoints() {
		return this._roundStore.selectedRound?.tiebreakerPoints || 0;
	}

	get tiebreakerValue() {
		return this._tiebreakerValue;
	}

	get margin() {
		return this._teamStore.margin;
	}

	closeModal = () => {
		this._teamStore.setIsTiebreakerVisible(false);
	};

	setMargin = (marginEu: number) => {
		this._tiebreakerValue = marginEu;

		const marginUs = this.tiebreakerPoints - marginEu;

		void this._teamStore.setMargin(marginEu, marginUs);
	};

	private messageByGameday = (index: number) => {
		return {
			0: this.i18n.t("modal.team_saved.description_day_1", "Message Day 1"),
			1: this.i18n.t("modal.team_saved.description_day_2", "Message Day 2"),
			2: this.i18n.t("modal.team_saved.description_day_3", "Message Day 3"),
		}[index];
	};

	onSaveTiebreaker = () => {
		const message = this.messageByGameday(
			Number(this._roundStore.selectedRoundIndex) ?? 0
		) as string;
		void this._teamStore.saveTiebreaker();

		this._modalsStore.showModal(ModalType.TEAM_SAVED, {
			title: this.i18n.t("modal.team_saved.title", "Team Saved"),
			message: message,
			callback: () => {
				this._modalsStore.hideModal();
			},
		});
	};

	dispose() {
		return;
		// void this._disposerReaction;
	}

	init(param: void) {
		return;
	}
}
